import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingCart,
  ArrowLeft,
  Trash2,
  Plus,
  Minus,
  Package,
  CreditCard,
  Truck,
  Tag,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { useCart } from '../contexts/CartContext';
import Navbar from './Navbar';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, symbol, currency } = useCart();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [discount, setDiscount] = useState(null); // { code, percent }
  const [validatingCode, setValidatingCode] = useState(false);

  const priceField = currency === 'eur' ? 'price_eur' : 'price_usd';
  const discountAmount = discount ? (subtotal * discount.percent) / 100 : 0;
  const total = subtotal - discountAmount;

  const applyCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setValidatingCode(true);
    try {
      const res = await axios.get(`${API}/tools/discount/${encodeURIComponent(code)}`);
      setDiscount({ code: res.data.code, percent: res.data.percent });
      toast.success(`Código aplicado: ${res.data.percent}% de descuento`);
    } catch (err) {
      setDiscount(null);
      toast.error('Código de descuento no válido');
    } finally {
      setValidatingCode(false);
    }
  };

  const removeCode = () => {
    setDiscount(null);
    setCodeInput('');
  };

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    if (!email.match(/^\S+@\S+\.\S+$/)) {
      toast.error('Por favor introduce un email válido');
      return;
    }
    if (!name.trim()) {
      toast.error('Por favor introduce tu nombre');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tools/checkout`, {
        items: items.map((i) => ({ tool_id: i.id, quantity: i.quantity })),
        currency,
        customer_email: email.trim(),
        customer_name: name.trim(),
        origin_url: window.location.origin,
        discount_code: discount ? discount.code : null,
      });
      const url = res.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('No se pudo generar el enlace de pago');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err?.response?.data?.detail || 'Error al iniciar el pago');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      <Navbar />
      <div className="pt-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Link
          to="/tienda"
          data-testid="cart-back-store"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors mb-2"
        >
          <ArrowLeft size={14} /> Seguir comprando
        </Link>
        <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
          <ShoppingCart size={32} className="text-red-600" />
          Tu Carrito
        </h1>
        <p className="text-slate-600 mb-8">
          Revisa tu pedido antes de pagar. El envío internacional se cotiza por separado.
        </p>

        {items.length === 0 ? (
          <Card className="border-0 shadow-md" data-testid="cart-empty">
            <CardContent className="p-12 text-center">
              <Package size={64} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">Tu carrito está vacío.</p>
              <Link to="/tienda">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  Ver herramientas
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-[1fr_400px] gap-6">
            {/* Items */}
            <div className="space-y-3" data-testid="cart-items">
              {items.map((it) => (
                <Card
                  key={it.id}
                  data-testid={`cart-item-${it.id}`}
                  className="border-0 shadow-sm overflow-hidden"
                >
                  <CardContent className="p-4 flex gap-4 items-center">
                    <div
                      className="w-20 h-20 sm:w-24 sm:h-24 bg-slate-100 rounded-md bg-cover bg-center flex-shrink-0"
                      style={{ backgroundImage: `url(${resolveImg(it.image)})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-bold text-red-600">{it.id}</p>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base leading-tight line-clamp-2">
                        {it.name}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        {symbol}
                        {it[priceField].toFixed(2)} c/u
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center border rounded-md">
                        <button
                          data-testid={`cart-decr-${it.id}`}
                          onClick={() => updateQty(it.id, it.quantity - 1)}
                          className="p-1.5 hover:bg-slate-100"
                          aria-label="Disminuir"
                        >
                          <Minus size={14} />
                        </button>
                        <span
                          data-testid={`cart-qty-${it.id}`}
                          className="px-3 text-sm font-semibold tabular-nums min-w-[2rem] text-center"
                        >
                          {it.quantity}
                        </span>
                        <button
                          data-testid={`cart-incr-${it.id}`}
                          onClick={() => updateQty(it.id, it.quantity + 1)}
                          className="p-1.5 hover:bg-slate-100"
                          aria-label="Aumentar"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.id}`}
                        onClick={() => removeItem(it.id)}
                        className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Quitar
                      </button>
                    </div>
                    <div className="hidden sm:block text-right min-w-[80px]">
                      <p className="font-bold text-slate-900">
                        {symbol}
                        {(it[priceField] * it.quantity).toFixed(2)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Summary */}
            <Card className="border-0 shadow-xl h-fit sticky top-24" data-testid="cart-summary">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Resumen</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-700">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-semibold tabular-nums">
                      {symbol}
                      {subtotal.toFixed(2)}
                    </span>
                  </div>
                  {discount && (
                    <div className="flex justify-between text-green-700">
                      <span className="flex items-center gap-1">
                        Descuento ({discount.code} · {discount.percent}%)
                      </span>
                      <span data-testid="cart-discount" className="font-semibold tabular-nums">
                        −{symbol}
                        {discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-500 text-xs">
                    <span className="flex items-center gap-1">
                      <Truck size={12} /> Envío internacional
                    </span>
                    <span>Se cotiza después</span>
                  </div>
                </div>

                {/* Discount code */}
                <div className="mt-4">
                  {discount ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <span className="text-xs font-semibold text-green-800 flex items-center gap-1">
                        <Tag size={12} /> {discount.code} aplicado
                      </span>
                      <button
                        data-testid="cart-remove-code"
                        onClick={removeCode}
                        className="text-xs text-slate-500 hover:text-red-600"
                      >
                        Quitar
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        data-testid="cart-code-input"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyCode()}
                        placeholder="Código de descuento"
                        className="text-sm uppercase"
                      />
                      <Button
                        data-testid="cart-apply-code"
                        onClick={applyCode}
                        disabled={validatingCode || !codeInput.trim()}
                        variant="outline"
                        className="border-slate-300 whitespace-nowrap"
                      >
                        {validatingCode ? '...' : 'Aplicar'}
                      </Button>
                    </div>
                  )}
                </div>
                <hr className="my-4" />
                <div className="flex justify-between items-end text-slate-900 mb-6">
                  <span className="font-semibold">Total a pagar ahora</span>
                  <span
                    data-testid="cart-total"
                    className="text-2xl font-bold tabular-nums text-red-600"
                  >
                    {symbol}
                    {total.toFixed(2)}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">
                      Nombre completo *
                    </label>
                    <Input
                      data-testid="cart-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">
                      Email *
                    </label>
                    <Input
                      data-testid="cart-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <Button
                  data-testid="cart-checkout-btn"
                  onClick={handleCheckout}
                  disabled={submitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-base font-semibold"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                      Redirigiendo a Stripe...
                    </>
                  ) : (
                    <>
                      <CreditCard size={18} className="mr-2" />
                      Pagar con tarjeta
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-3">
                  🔒 Pago seguro con Stripe. Recogemos tu dirección durante el checkout.
                </p>
                <p className="text-xs text-slate-400 text-center mt-2">
                  El coste de envío internacional se calculará tras la compra y se cobrará aparte.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Cart;
